import { issuerName, restoreMonths } from '@domain/legal/IssuerInfo';
import { getIssuerInfo } from '@infra/legal/IssuerInfoService';
import { z } from 'zod';
import { SecureLicenceStore } from '@infra/licence/SecureLicenceStore';
import { LicenceStatusService } from '@infra/licence/LicenceStatusService';
import {
  resolveHybridBackupEndpoints,
  resolveHybridBackupItemEndpoints,
  resolveHybridBackupLatestMetaEndpoints,
  resolveHybridBackupListEndpoints,
} from '@infra/licence/activationConstants';
import { resilientFetch } from '@infra/network/resilientFetch';
import type { HybridEnvelope } from './HybridBackupCrypto';

export const hybridBackupMetaSchema = z.object({
  id: z.string().min(1).max(200),
  created_at: z.string().min(1).max(64),
  size_bytes: z.number().int().nonnegative().optional(),
  app_version: z.string().max(64).optional(),
  schema_version: z.number().int().optional(),
  sha256: z
    .string()
    .regex(/^[0-9a-fA-F]{64}$/)
    .optional(),
});
export type HybridBackupMeta = z.infer<typeof hybridBackupMetaSchema>;

const listSchema = z.union([
  z.array(hybridBackupMetaSchema),
  z.object({ backups: z.array(hybridBackupMetaSchema) }).transform((v) => v.backups),
]);

/** Codes d'erreur stables renvoyés par le serveur (corps JSON `error`). */
export type HybridServerErrorCode =
  | 'restore_window_closed'
  | 'license_revoked'
  | 'integrity_error'
  | 'not_found'
  | (string & {});

export type HybridTransportErrorCode = 'NO_LICENCE' | 'NETWORK' | 'AUTH' | 'NOT_FOUND' | 'SERVER' | 'BAD_RESPONSE';

export class HybridTransportError extends Error {
  constructor(
    readonly code: HybridTransportErrorCode,
    message: string,
    readonly status?: number,
    readonly serverCode?: HybridServerErrorCode,
  ) {
    super(message);
    this.name = 'HybridTransportError';
  }
}

/** Port transport : permet aux tests d'utiliser un faux serveur sans réseau. */
export interface HybridBackupTransport {
  upload(envelope: HybridEnvelope, options: { notify: boolean }): Promise<void>;
  fetchLatestMeta(): Promise<HybridBackupMeta | null>;
  list(): Promise<HybridBackupMeta[]>;
  download(id: string): Promise<{ bytes: Uint8Array; sha256: string | null }>;
}

async function authHeaders(): Promise<Record<string, string>> {
  const jwt = await SecureLicenceStore.getActivationCertificate();
  const fingerprint = await SecureLicenceStore.getDeviceFingerprint();
  if (!jwt || !fingerprint) throw new HybridTransportError('NO_LICENCE', 'Licence locale absente');
  return { Authorization: `Bearer ${jwt}`, 'X-Device-Fingerprint': fingerprint };
}

async function readServerCode(res: Response): Promise<string | undefined> {
  try {
    // Contrat serveur : `code`, puis `error_code`, puis `error` (libellé FR en dernier recours).
    const body: unknown = await res.clone().json();
    const code = LicenceStatusService.extractErrorCode(body);
    return typeof code === 'string' && /^[a-z_]{1,64}$/.test(code) ? code : undefined;
  } catch {
    return undefined;
  }
}

/** Message clair (français) pour l'interface, selon le code serveur ou le statut. */
export function describeHybridTransportError(err: unknown): string {
  if (!(err instanceof HybridTransportError)) {
    return 'Erreur inattendue pendant l’accès aux sauvegardes.';
  }
  const info = getIssuerInfo();
  const name = issuerName(info);
  const months = restoreMonths(info);
  switch (err.serverCode) {
    case 'restore_window_closed':
      return `Le délai de restauration est dépassé : votre licence n’est plus active depuis plus longtemps que le délai de restauration${months ? ` (${String(months)} mois)` : ''}. Contactez ${name} pour la renouveler.`;
    case 'license_revoked':
      return `Cette licence a été révoquée : la restauration n’est pas autorisée. Contactez ${name}.`;
    case 'integrity_error':
      return `Le serveur a détecté une altération de cette sauvegarde. Choisissez une sauvegarde plus ancienne ou contactez ${name}.`;
    case 'not_found':
      return 'Aucune sauvegarde trouvée pour cette licence.';
    default:
  }
  switch (err.code) {
    case 'NOT_FOUND':
      return 'Aucune sauvegarde trouvée pour cette licence.';
    case 'AUTH':
      return 'Accès refusé : vérifiez que ce poste est bien activé avec la licence Hybride.';
    case 'NO_LICENCE':
      return 'Ce poste n’est pas activé : activez la licence avant d’accéder aux sauvegardes.';
    case 'NETWORK':
      return 'Serveur injoignable : vérifiez votre connexion Internet et réessayez.';
    case 'BAD_RESPONSE':
      return 'Réponse du serveur illisible. Réessayez dans quelques instants.';
    case 'SERVER':
      return 'Le serveur de sauvegarde a répondu par une erreur. Réessayez plus tard.';
    default:
      return 'Le serveur de sauvegarde a répondu par une erreur. Réessayez plus tard.';
  }
}

function classify(status: number): HybridTransportErrorCode {
  if (status === 401 || status === 403) return 'AUTH';
  if (status === 404) return 'NOT_FOUND';
  return 'SERVER';
}

async function tryEndpoints(
  endpoints: string[],
  init: RequestInit,
  timeoutMs: number,
): Promise<Response> {
  let last: HybridTransportError = new HybridTransportError('NETWORK', 'Serveur injoignable');
  for (const endpoint of endpoints) {
    try {
      const res = await resilientFetch(endpoint, init, { timeoutMs, retries: 0 });
      if (res.ok) return res;
      const serverCode = await readServerCode(res);
      last = new HybridTransportError(classify(res.status), `Réponse serveur ${String(res.status)}`, res.status, serverCode);
      // erreurs métier stables : inutile d'essayer un autre serveur
      if (last.code === 'AUTH' || last.code === 'NOT_FOUND' || serverCode === 'restore_window_closed' || serverCode === 'integrity_error') throw last;
    } catch (err) {
      if (err instanceof HybridTransportError) {
        if (err.code === 'AUTH' || err.code === 'NOT_FOUND' || err.serverCode) throw err;
        last = err;
      } else {
        last = new HybridTransportError('NETWORK', err instanceof Error ? err.message : 'Serveur injoignable');
      }
    }
  }
  throw last;
}

function readSha256Header(res: Response): string | null {
  const direct = res.headers.get('x-backup-sha256') ?? res.headers.get('x-content-sha256');
  if (direct && /^[0-9a-fA-F]{64}$/.test(direct)) return direct.toLowerCase();
  const etag = res.headers.get('etag')?.replace(/^W\//, '').replace(/"/g, '');
  if (!direct && etag && /^[0-9a-fA-F]{64}$/.test(etag)) return etag.toLowerCase();
  const digest = res.headers.get('digest');
  const m = digest ? /sha-256=([A-Za-z0-9+/=]+)/i.exec(digest) : null;
  if (m) {
    const bin = atob(m[1]!);
    return Array.from(bin, (c) => c.charCodeAt(0).toString(16).padStart(2, '0')).join('');
  }
  return null;
}

export const defaultHybridBackupTransport: HybridBackupTransport = {
  async upload(envelope, options) {
    const headers = await authHeaders();
    await tryEndpoints(
      resolveHybridBackupEndpoints(),
      {
        method: 'POST',
        headers: {
          ...headers,
          'Content-Type': 'application/json',
          'X-Hybrid-Notify': options.notify ? '1' : '0',
        },
        body: JSON.stringify(envelope),
      },
      60_000,
    );
  },

  async fetchLatestMeta() {
    const headers = await authHeaders();
    try {
      const res = await tryEndpoints(resolveHybridBackupLatestMetaEndpoints(), { method: 'GET', headers }, 10_000);
      return hybridBackupMetaSchema.parse(await res.json());
    } catch (err) {
      if (err instanceof HybridTransportError && err.code === 'NOT_FOUND') return null;
      if (err instanceof z.ZodError) throw new HybridTransportError('BAD_RESPONSE', 'Réponse serveur invalide');
      throw err;
    }
  },

  async list() {
    const headers = await authHeaders();
    const res = await tryEndpoints(resolveHybridBackupListEndpoints(), { method: 'GET', headers }, 15_000);
    try {
      return listSchema.parse(await res.json());
    } catch {
      throw new HybridTransportError('BAD_RESPONSE', 'Réponse serveur invalide');
    }
  },

  async download(id) {
    const headers = await authHeaders();
    const res = await tryEndpoints(resolveHybridBackupItemEndpoints(id), { method: 'GET', headers }, 120_000);
    return { bytes: new Uint8Array(await res.arrayBuffer()), sha256: readSha256Header(res) };
  },
};
